import { FileReference, SDate, SDuration } from '@summit/core';
import { DurationFormatter, SummitFormattedText } from '@summit/models';

import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { EboEventTo } from '../base-classes/ebo-event-to';
import { Division } from '../division/division';
import { Employee } from '../employee/employee';
import { Company } from '../organization/company/company';
import { Hub } from '../places/hub/hub';
import { Truck } from '../truck/truck';
import { MaintEventItem } from './maint-event-item';
import { MaintEventStatus, MaintEventStatusMapping } from './maint-event-status.enum';
import { MaintEventTimeLog } from './maint-event-time-log';

export class MaintEvent extends EboEventTo {
  name: string;
  inHouse: boolean;
  truckID: string;
  truck: Truck;
  divisionID: string;
  division: Division;
  status: MaintEventStatus;
  mechanicId: string;
  mechanic: Employee;
  maintCompanyId: string;
  maintCompany: Company;
  odometer: number;
  amount: number;
  items: MaintEventItem[];
  files?: FileReference[];
  timeLogs?: MaintEventTimeLog[];

  //Truck Search Fields
  truckNumberSearch: string;

  //cache
  private _attachments: SummitFormattedText;

  constructor(jsonData?: Partial<MaintEvent>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.division) this.division = new Division(jsonData.division);
      if (jsonData.maintCompany) this.maintCompany = new Company(jsonData.maintCompany);
      if (jsonData.mechanic) this.mechanic = new Employee(jsonData.mechanic);
      if (jsonData.truck) this.truck = new Truck(jsonData.truck);
      if (jsonData.items) this.items = jsonData.items.map((item) => new MaintEventItem(item));
      if (jsonData.files) this.files = jsonData.files.map((file) => new FileReference(file));
      if (jsonData.timeLogs)
        this.timeLogs = jsonData.timeLogs
          .map((timeLog) => new MaintEventTimeLog(timeLog))
          .sort((a, b) => {
            return a.startTime.isBefore(b.startTime) ? -1 : 1;
          });
    }
    this.inHouse ??= false;
    if (!this.inHouse) this.status ??= MaintEventStatus.Complete;
    this.eventDate ??= SDate.getNormalized();
    this.items ??= [];
    this.files ??= [];
    this.timeLogs ??= [];
    //console.log(`MaintEvent created with id: ${this.id}, eventDate: ${this.eventDate?.format()}`);
  }

  get fedexNumber(): SummitFormattedText {
    return this.truck?.fedexNumber;
  }
  get domicile(): Hub {
    return (
      this.truck?.hub ??
      new Hub({
        id: 0,
        name: ' Unknown Hub',
        abbreviation: 'UNK'
      })
    );
  }

  get hasMmrItems(): boolean {
    return this.items?.some((item) => item.includeInMMR ?? item.maintItem?.includeOnMMR ?? false);
  }

  get attachments(): SummitFormattedText {
    if (this._attachments === undefined) {
      if (!(this.files?.length > 0)) this._attachments = null;
      else {
        this._attachments = new SummitFormattedText();
        this.files.forEach((file) => {
          this._attachments.AddLine(file.name);
        });
      }
    }
    return this._attachments;
  }

  get amountSummary(): number {
    if (this.amount) return this.amount;
    if (this.items?.length > 0) {
      const subTotal = this.items.reduce((a, b) => a + (b?.amount ?? 0), 0);
      return subTotal ? subTotal : undefined;
    }
    return undefined;
  }

  private _truckHeader: string;
  get truckHeader(): string {
    if (this._truckHeader === undefined) {
      const response = [];
      if (this.truck) {
        if (this.truck.activeAliasAssignments && this.truck.activeAliasAssignments.length > 0) {
          response.push(`${this.truck.displayName}:`);
          response.push(
            this.truck.activeAliasAssignments
              .map((alias) => alias.alias.name)
              .sort()
              .join('/')
          );
        } else {
          response.push(this.truck.displayName);
        }
      }
      this._truckHeader = response.join(' ');
      if (this._truckHeader === '') {
        this._truckHeader = null;
      }
    }
    return this._truckHeader;
  }

  private _statusSummary: SummitFormattedText;
  get statusSummary(): SummitFormattedText {
    if (this._statusSummary === undefined) {
      this._statusSummary = new SummitFormattedText();
      this._statusSummary.AddLine(MaintEventStatusMapping[this.status], [
        'text-lg',
        'flex',
        'grow',
        'justify-center',
        'align-items-center',
        'text-bold'
      ]);
      this._statusSummary.AddKeyValues('Assigned', this.mechanic?.toShortString());
      this.timeLogs?.forEach((timeLog) => {
        const endFormat = timeLog.endTime?.isSame(timeLog.startTime, 'day') ? 'HH:mm' : 'M/D HH:mm';
        const durationString = timeLog.endTime
          ? dayjs.duration(timeLog.endTime.local().diff(timeLog.startTime.local())).humanize()
          : dayjs.duration(SDate.NowLocal().diff(timeLog.startTime.local())).humanize();
        this._statusSummary.AddLine(
          `${timeLog.startTime.local().format('M/D HH:mm')} - ${
            timeLog.endTime?.local().format(endFormat) ?? 'Ongoing'
          } (${durationString})`,
          ['flex', 'grow', 'justify-center', 'align-items-center']
        );
        this._statusSummary.AddKeyValues('Mechanic', timeLog.mechanic?.toShortString());
        if (timeLog.notes) {
          this._statusSummary.AddHtml(timeLog.notes);
        }
      });
    }
    return this._statusSummary;
  }

  private _totalMechanicTime: Duration;
  get totalMechanicTime(): Duration {
    if (this._totalMechanicTime === undefined) {
      this._totalMechanicTime = this.timeLogs.reduce((acc: Duration, timeLog) => {
        return acc.add(timeLog.duration);
      }, SDuration.new());
    }
    return this._totalMechanicTime;
  }

  private _timeByMechanic: SummitFormattedText;
  get timeByMechanic(): SummitFormattedText {
    if (this._timeByMechanic === undefined) {
      this._timeByMechanic = new SummitFormattedText();
      const mechanicNames = this.timeLogs.map((timeLog) => timeLog.mechanic?.toShortString());
      const uniqueMechanicNames = [...new Set(mechanicNames)];
      uniqueMechanicNames.forEach((mechanicName) => {
        const mechanicTime = this.timeLogs
          .filter((timeLog) => timeLog.mechanic?.name === mechanicName)
          .reduce((acc: Duration, timeLog) => {
            return acc.add(timeLog.duration);
          }, SDuration.new());
        this._timeByMechanic.AddKeyValues(mechanicName, DurationFormatter(mechanicTime));
      });
    }
    return this._timeByMechanic;
  }

  toShortString(): string {
    return this.name ?? '[Unnamed]';
  }
}
